import { getFirebaseBackend } from '../../authUtils.js'
import { updateUserProfile } from '@/helpers/database/user.js';
import { doc, onSnapshot, collection, query, where, updateDoc } from "firebase/firestore"; // Added collection, query, where
import { getAuth } from "firebase/auth"; // Import getAuth
import { db } from "@/helpers/firebase";
import i18n from "@/i18n";

let unsubscribeUserProfileListener = null;
let unsubscribeSupportTicketsListener = null; // Listener for unread support tickets

// Sync locale from Firestore appLanguage if it differs from current
function syncLocaleFromFirestore(userData) {
  if (userData?.appLanguage && i18n.global.availableLocales.includes(userData.appLanguage)) {
    const currentLocale = i18n.global.locale;
    const savedLocale = localStorage.getItem('mysticlog-lang');
    if (userData.appLanguage !== currentLocale && userData.appLanguage !== savedLocale) {
      i18n.global.locale = userData.appLanguage;
      localStorage.setItem('mysticlog-lang', userData.appLanguage);
    }
  }
}

export const state = {
    currentUser: JSON.parse(localStorage.getItem('authUser')) || null,
    firestoreUser: JSON.parse(localStorage.getItem('firestoreUser')) || null, // <-- Add state for Firestore data
    isAdmin: localStorage.getItem('isAdmin') === 'true' || false, // New state for admin status
    unreadSupportMessagesCount: 0, // New state for unread support messages
    loading: false,
    error: null
};

export const mutations = {
    SET_CURRENT_USER(state, user) {
        state.currentUser = user;
        state.loading = false;
        state.error = null;
        // Store Firebase Auth user in session storage
        if (user) {
            localStorage.setItem('authUser', JSON.stringify(user));
        } else {
            localStorage.removeItem('authUser');
        }
        //saveState('auth.currentUser', user)
    },
    SET_FIRESTORE_USER(state, userData) { // <-- Add mutation for Firestore data
        state.firestoreUser = userData;
        // Optionally store Firestore user data in session storage too
        if (userData) {
            localStorage.setItem('firestoreUser', JSON.stringify(userData));
        } else {
            localStorage.removeItem('firestoreUser');
        }
    },
    SET_ADMIN_STATUS(state, isAdmin) { // New mutation for admin status
        state.isAdmin = isAdmin;
        if (isAdmin) {
            localStorage.setItem('isAdmin', 'true');
        } else {
            localStorage.removeItem('isAdmin');
        }
    },
    SET_UNREAD_SUPPORT_MESSAGES_COUNT(state, count) { // New mutation
        state.unreadSupportMessagesCount = count;
    },
    UPDATE_FIRESTORE_USER_DETAILS(state, updatedData) {
        if (state.firestoreUser) {
          state.firestoreUser = { ...state.firestoreUser, ...updatedData };
          localStorage.setItem('firestoreUser', JSON.stringify(state.firestoreUser));
        } else {
          console.warn("Attempted to update details on a null firestoreUser");
          state.firestoreUser = updatedData;
          localStorage.setItem('firestoreUser', JSON.stringify(state.firestoreUser));
        }
      },    
    SET_LOADING(state, loading) {
        state.loading = loading;
        state.error = null;
    },
    SET_ERROR(state, error) {
        state.error = error;
        state.loading = false;
        state.currentUser = null;
        state.firestoreUser = null; // <-- Clear Firestore user on error too
        state.isAdmin = false; // Clear admin status on error
        state.unreadSupportMessagesCount = 0;
        localStorage.removeItem('authUser');
        localStorage.removeItem('firestoreUser');
        localStorage.removeItem('isAdmin');
    },
    CLEAR_AUTH_STATE(state) { // <-- Renamed for clarity, clears everything
        // ... existing clears ...
        state.currentUser = null;
        state.firestoreUser = null;
        state.loading = false;
        state.error = null;
        state.isAdmin = false; // Clear admin status
        state.unreadSupportMessagesCount = 0; // Clear unread count
        localStorage.removeItem('authUser');
        localStorage.removeItem('firestoreUser');
        localStorage.removeItem('isAdmin');
    }    
}

export const getters = {
    currentUser: state => state.currentUser,
    firestoreUser: state => state.firestoreUser, // <-- Add getter
    loading: state => state.loading,
    isAdmin: state => state.isAdmin, // New getter for admin status
    error: state => state.error,
    loggedIn: state => !!state.currentUser, // Example getter
};

export const actions = {
    // This is automatically run in `src/state/store.js` when the app
    // starts, along with any other actions named `init` in other modules.
    // eslint-disable-next-line no-unused-vars 
    async init({ state, dispatch }) { // Make init async
        dispatch('validate')
    },

    // Logs in the current user.
    // eslint-disable-next-line no-unused-vars
    async logIn({ commit, dispatch }, { email, password, with_provider }) {
        commit('SET_LOADING', true);
        // ---> Unsubscribe previous listener if any <---
        if (unsubscribeUserProfileListener) {
            //console.log('[Vuex auth/logIn] Unsubscribing previous user listener.');
            unsubscribeUserProfileListener();
            unsubscribeUserProfileListener = null;
        }
        // ---> End Unsubscribe <---
        try {
            const firebaseBackend = getFirebaseBackend();
            const user = await firebaseBackend.loginUser(email, password, with_provider);
            commit('SET_CURRENT_USER', user);

            // --- Process Admin Claim ---
            if (user) {
                const idTokenResult = await user.getIdTokenResult();
                commit('SET_ADMIN_STATUS', idTokenResult.claims.admin === true);
            } else {
                commit('SET_ADMIN_STATUS', false);
            }

            if (user) {
                // --- Fetch INITIAL Firestore User Data ---
                const firestoreUserData = await firebaseBackend.getUserDocument(); // Assumes this fetches based on user.uid
                if (firestoreUserData) {
                    commit('SET_FIRESTORE_USER', firestoreUserData);
                    // Sync Firestore appLanguage to i18n on login
                    syncLocaleFromFirestore(firestoreUserData);
                } else {
                    console.warn("[Vuex auth/logIn] Firestore user document not found for UID:", user.uid);
                    commit('SET_FIRESTORE_USER', null);
                }

                // ---> SETUP REAL-TIME LISTENER <---
                const userDocRef = doc(db, "users", user.uid); // Adjust "users" if your collection name is different
                //console.log(`[Vuex auth/logIn] Setting up Firestore listener for user: ${user.uid}`);

                unsubscribeUserProfileListener = onSnapshot(userDocRef, (docSnap) => {
                    if (docSnap.exists()) {
                        //console.log("[Vuex auth/onSnapshot] Received user data update:", docSnap.data());
                        // Commit the latest data whenever it changes
                        commit('SET_FIRESTORE_USER', { id: docSnap.id, ...docSnap.data() });
                        syncLocaleFromFirestore(docSnap.data());
                    } else {
                        console.warn("[Vuex auth/onSnapshot] User document deleted or no longer exists.");
                        commit('SET_FIRESTORE_USER', null);
                    }
                }, (error) => {
                    console.error("[Vuex auth/onSnapshot] Error listening to user profile:", error);
                    // Handle listener error, maybe clear user data or set an error state
                    commit('SET_FIRESTORE_USER', null); // Clear data on listener error
                    // Optionally commit a specific error state if needed
                    // commit('SET_ERROR', 'Failed to listen to profile updates.');
                });
                // ---> END SETUP LISTENER <---
                // Setup listener for unread support tickets
                dispatch('listenForUnreadSupportTickets', user.uid);

            } else {
                 commit('SET_FIRESTORE_USER', null); // Clear if login failed
            }

            return user;
        } catch (error) {
            commit('SET_ERROR', error.toString());
            // ---> Ensure listener is cleared on login error too <---
            if (unsubscribeUserProfileListener) {
                unsubscribeUserProfileListener();
                unsubscribeUserProfileListener = null;
            }
            if (unsubscribeSupportTicketsListener) { // Clear support listener on error too
                unsubscribeSupportTicketsListener();
                unsubscribeSupportTicketsListener = null;
            }
            // ---> End Ensure <---
            throw error;
        }
    },

    async logOut({ commit }) {
        const userId = state.currentUser?.uid; // Get userId BEFORE clearing state

        // ---> Unsubscribe from the listener FIRST <---
        if (unsubscribeUserProfileListener) {
            //console.log('[Vuex auth/logOut] Unsubscribing user listener.');
            unsubscribeUserProfileListener();
            unsubscribeUserProfileListener = null; // Clear the reference
        }
        if (unsubscribeSupportTicketsListener) { // Clear support listener on logout
            unsubscribeSupportTicketsListener();
            unsubscribeSupportTicketsListener = null;
        }


        // --- Clear User-Specific Cache ---
        if (userId) {
            const querentCacheKey = `querentList_${userId}`;
            const readingTypesCacheKey = `readingTypesList_${userId}`;
            console.log(`[Vuex auth/logOut] Clearing localStorage keys: professionalReadings, ${querentCacheKey}, ${readingTypesCacheKey}`);
            localStorage.removeItem('professionalReadings');
            localStorage.removeItem(querentCacheKey);
            localStorage.removeItem(readingTypesCacheKey);
            // Note: 'mysticlog-lang' is kept as it's a browser preference.
        }
        // ---> End Unsubscribe <---

        const firebaseBackend = getFirebaseBackend();
        await firebaseBackend.logout();
        commit('CLEAR_AUTH_STATE'); // Clears currentUser, firestoreUser, etc.
    },  

    // eslint-disable-next-line no-unused-vars
    async register({ commit, dispatch }, { username, email, password }) {
        commit('SET_LOADING', true);
        try {
            const firebaseBackend = getFirebaseBackend();
            const user = await firebaseBackend.registerUser(username, email, password);
            commit('SET_CURRENT_USER', user);
            commit('SET_FIRESTORE_USER', null); // Clear potential stale data
            return user;
        } catch (error) {
            commit('SET_ERROR', error.toString());
            throw error;
        }
    },    

    async resetPassword({ commit }, { email }) {
        commit('SET_LOADING', true);
        try {
            const firebaseBackend = getFirebaseBackend();
            await firebaseBackend.forgetPassword(email);
            commit('SET_LOADING', false); // Reset loading after request
        } catch (error) {
            commit('SET_ERROR', error.toString()); // Keep error state for feedback
            throw error;
        }
    },    

    // eslint-disable-next-line no-unused-vars
    async validate({ commit, state, dispatch }) { // Make validate async
        //console.log('[Vuex auth/validate] Action executing...');
        const firebaseBackend = getFirebaseBackend();
        const userFromStorage = firebaseBackend.getAuthenticatedUser(); // User from localStorage

        if (unsubscribeUserProfileListener) {
            //console.log('[Vuex auth/validate] Unsubscribing previous user listener.');
            unsubscribeUserProfileListener();
            unsubscribeUserProfileListener = null;
        }
        if (unsubscribeSupportTicketsListener) { // Clear support listener if validate clears auth state
            unsubscribeSupportTicketsListener();
            unsubscribeSupportTicketsListener = null;
        }

        if (userFromStorage) {
            //console.log('[Vuex auth/validate] User found in session, committing SET_CURRENT_USER');
            commit('SET_CURRENT_USER', userFromStorage); // Commit user from localStorage

            // --- Process Admin Claim on validation ---
            const liveAuthUser = getAuth().currentUser; // Get the LIVE Firebase Auth user object
            if (liveAuthUser) { // Check if the live user object is available
                try {
                    const idTokenResult = await liveAuthUser.getIdTokenResult(true); // Force refresh if needed
                    commit('SET_ADMIN_STATUS', idTokenResult.claims.admin === true);
                } catch (error) {
                    console.error("Error getting ID token result during validation:", error);
                    commit('SET_ADMIN_STATUS', false);
                }
            } else {
                // This can happen if localStorage has a user, but Firebase Auth SDK hasn't fully initialized
                // or if the session is invalid/expired and currentUser is now null.
                console.warn("[Vuex auth/validate] User found in localStorage, but getAuth().currentUser is null. Admin status set to false.");
                commit('SET_ADMIN_STATUS', false);
            }


            // Setup Firestore listener using the UID from the stored user object
            const userDocRef = doc(db, "users", userFromStorage.uid);
            //console.log(`[Vuex auth/validate] Setting up Firestore listener for user: ${user.uid}`);

            unsubscribeUserProfileListener = onSnapshot(userDocRef, (docSnap) => {
                if (docSnap.exists()) {
                    //console.log("[Vuex auth/onSnapshot-validate] Received user data update:", docSnap.data());
                    commit('SET_FIRESTORE_USER', { id: docSnap.id, ...docSnap.data() });
                } else {
                    console.warn("[Vuex auth/onSnapshot-validate] User document not found during validation.");
                    commit('SET_FIRESTORE_USER', null);
                }
            }, (error) => {
                console.error("[Vuex auth/onSnapshot-validate] Error listening to user profile:", error);
                commit('SET_FIRESTORE_USER', null);
               
            });

            // Setup listener for unread support tickets
            dispatch('listenForUnreadSupportTickets', userFromStorage.uid);

        } else {
            //console.log('[Vuex auth/validate] No user found in session, committing CLEAR_AUTH_STATE');
            commit('CLEAR_AUTH_STATE');
        }

        return userFromStorage; // Return the user object from storage as before
    },  
    // eslint-disable-next-line no-unused-vars
    async updateUserProfileData({ commit, state, dispatch }, profileData) {
        if (!state.currentUser?.uid) {
            throw new Error("User not authenticated.");
        }

        const firebaseBackend = getFirebaseBackend();
        const currentAuthUser = state.currentUser; // Get user from Vuex state
        const userId = currentAuthUser.uid;
        const originalEmail = currentAuthUser.email;
        const newEmail = profileData.email; // Email from the form data

        try {
            // --- Step 1: Handle Email Change in Firebase Auth (if changed) ---
            if (newEmail && newEmail !== originalEmail) {
                //console.log(`Attempting to change email from ${originalEmail} to ${newEmail}`);
                try {
                    // Call the new method in authUtils
                    const updatedAuthUser = await firebaseBackend.updateUserEmail(newEmail);
                    // If successful, update the currentUser state immediately
                    commit('SET_CURRENT_USER', updatedAuthUser);
                    //console.log("Firebase Auth email updated, Vuex currentUser state refreshed.");
                } catch (error) {
                    console.error("Failed to update Firebase Auth email:", error);
                    // Specific error handling (example)
                    if (error.includes('auth/requires-recent-login')) {
                         // Option 1: Throw a specific error for the UI to handle (e.g., show re-auth modal)
                         throw new Error('REQUIRES_RECENT_LOGIN');
                         // Option 2: Directly trigger re-auth flow here (more complex)
                    } else if (error.includes('auth/email-already-in-use')) {
                         throw new Error('EMAIL_ALREADY_IN_USE');
                    } else {
                         throw new Error(`Failed to update authentication email: ${error}`);
                    }
                    // IMPORTANT: If email update fails, we stop here and don't update Firestore with the new email.
                }
            }

            // --- Step 2: Update Firestore Document ---
            //console.log("Updating Firestore profile data for user:", userId);
            // Ensure profileData contains the correct email (either original or newly verified one)
            // The profileData passed in already has the new email if the user changed it.
            // Since we updated Auth first, we proceed with this data.
            await updateUserProfile(userId, profileData);
            //console.log("Firestore profile updated successfully.");

            // --- Step 3: Update Vuex Firestore User State ---
            // This commit merges the changes into the firestoreUser state object
            commit('UPDATE_FIRESTORE_USER_DETAILS', profileData);
            //console.log("Vuex firestoreUser state updated after profile save.");

            // No need to return anything unless the component needs it

        } catch (error) {
            console.error("Error during profile update process:", error);
            // Re-throw the specific errors or a generic one for the component
            throw error;
        }
    },

    /**
     * Changes the user's password.
     * Requires old password for re-authentication.
     */
    async changeUserPassword({ commit }, { oldPassword, newPassword }) {
        commit('SET_LOADING', true); // Use general loading or a specific one
        const firebaseBackend = getFirebaseBackend();

        try {
            // Step 1: Re-authenticate using the OLD password
            //console.log("Attempting to re-authenticate user for password change...");
            await firebaseBackend.reauthenticateUser(oldPassword);
            //console.log("Re-authentication successful.");

            // Step 2: If re-authentication succeeds, update to the NEW password
            //console.log("Attempting to update password in Firebase Auth...");
            await firebaseBackend.updateUserPassword(newPassword);
            //console.log("Password updated successfully in Firebase Auth.");

            commit('SET_LOADING', false);
            // No state mutation needed here unless you store password-related flags

        } catch (error) {
            console.error("Password change failed:", error);
            // Error could be from re-authentication (wrong old password)
            // or from password update (e.g., weak password)
            commit('SET_ERROR', `Password change failed: ${error}`); // Set specific error
            commit('SET_LOADING', false);
            throw new Error(error); // Re-throw for the component to catch
        }
    },   
    
    /**
     * Sends a password reset email to the currently logged-in user's email address.
     */
    async requestPasswordReset({ commit, state }) {
        // Use a specific loading state if desired, or reuse general loading
        // commit('SET_PASSWORD_RESET_LOADING', true);
        const firebaseBackend = getFirebaseBackend();
        const userEmail = state.currentUser?.email; // Get email from current auth state

        if (!userEmail) {
            const errorMsg = "Cannot send password reset: User email not found in state.";
            console.error(errorMsg);
            commit('SET_ERROR', errorMsg); // Set general error or specific one
            // commit('SET_PASSWORD_RESET_LOADING', false);
            throw new Error(errorMsg);
        }

        try {
            //console.log(`Requesting password reset email for: ${userEmail}`);
            await firebaseBackend.sendPasswordReset(userEmail);
            //console.log("Password reset email request successful.");
            // commit('SET_PASSWORD_RESET_LOADING', false);
            // Optionally commit a success state if needed for global feedback
            // commit('SET_PASSWORD_RESET_SUCCESS', true);

        } catch (error) {
            console.error("Password reset request failed:", error);
            commit('SET_ERROR', `Password reset failed: ${error}`); // Set specific error
            // commit('SET_PASSWORD_RESET_LOADING', false);
            throw new Error(error); // Re-throw for the component to catch
        }
    },    

    async sendEmailVerification() {
        const firebaseBackend = getFirebaseBackend();
        const actionCodeSettings = {
            url: `${process.env.VUE_APP_BASE_URL || 'https://mysticlog.app'}/dashboard`,
        };
        try {
            await firebaseBackend.sendVerificationEmail(actionCodeSettings);
        } catch (error) {
            console.error("Failed to send email verification:", error);
            throw error;
        }
    },

    async refreshEmailVerification({ commit, state }) {
        if (!state.currentUser?.uid) {
            return state.currentUser;
        }
        const firebaseBackend = getFirebaseBackend();
        try {
            const refreshedUser = await firebaseBackend.reloadCurrentUser();
            const snapshot = refreshedUser.toJSON ? refreshedUser.toJSON() : { ...refreshedUser };
            commit('SET_CURRENT_USER', snapshot);
            // Mirror the flag to Firestore (non-blocking) so it can be queried/counted.
            if (snapshot?.uid) {
                try {
                    await updateDoc(doc(db, "users", snapshot.uid), { emailVerified: !!snapshot.emailVerified });
                } catch (syncError) {
                    console.warn("Failed to sync emailVerified to Firestore:", syncError);
                }
            }
            return snapshot;
        } catch (error) {
            console.error("Failed to refresh email verification:", error);
            throw error;
        }
    },

   /**
     * Permanently deletes the current user's account (EMAIL/PASSWORD users).
     * Requires password for re-authentication.
     */
   async deleteUserAccount({ commit, state }, { password }) { // <-- Add state
        const userId = state.currentUser?.uid; // <-- Get userId BEFORE clearing state
        commit('SET_LOADING', true);
        const firebaseBackend = getFirebaseBackend();
        try {
            //console.log("Attempting to re-authenticate user (password) for account deletion...");
            await firebaseBackend.reauthenticateUser(password);
            //console.log("Re-authentication successful for deletion.");

            //console.log("Attempting to delete user (password) from Firebase Auth...");
            await firebaseBackend.deleteCurrentUser();
            //console.log("Firebase user account deleted successfully.");

            // --- Clear Vuex State and LocalStorage ---
            commit('CLEAR_AUTH_STATE');
            if (userId) {
                const querentCacheKey = `querentList_${userId}`;
                const readingTypesCacheKey = `readingTypesList_${userId}`;
                console.log(`[Vuex auth/deleteUserAccount] Clearing localStorage keys: professionalReadings, ${querentCacheKey}, ${readingTypesCacheKey}`);
                localStorage.removeItem('professionalReadings');
                localStorage.removeItem(querentCacheKey);
                localStorage.removeItem(readingTypesCacheKey);
                // Note: 'mysticlog-lang' is kept.
            } else {
                 console.warn("[Vuex auth/deleteUserAccount] Could not get userId to clear specific cache keys.");
            }

            commit('SET_LOADING', false);
        } catch (error) {
            console.error("Account deletion failed (password user):", error);
            let specificErrorMessageKey = 'profile.errors.deleteAccountFailed'; // Default generic error

            if (error.code === 'auth/wrong-password') {
                specificErrorMessageKey = 'profile.errors.incorrectPassword';
            } else if (error.code === 'auth/too-many-requests') {
                specificErrorMessageKey = 'profile.errors.tooManyAttempts';
            } else if (error.code === 'auth/requires-recent-login') {
                specificErrorMessageKey = 'profile.errors.reauthNeededForDelete';
            }
            commit('SET_ERROR', specificErrorMessageKey);
            commit('SET_LOADING', false);
            throw new Error(specificErrorMessageKey); // Throw the key
        }
    },

    /**
     * Permanently deletes the current user's account (OAUTH users).
     * Attempts direct deletion without password re-authentication.
     */
    async deleteOAuthUserAccount({ commit, state }) { // <-- Add state
        const userId = state.currentUser?.uid; // <-- Get userId BEFORE clearing state
        commit('SET_LOADING', true);
        const firebaseBackend = getFirebaseBackend();
        try {
            //console.log("Attempting to delete user (OAuth) from Firebase Auth directly...");
            await firebaseBackend.deleteCurrentUser();
            //console.log("Firebase user account (OAuth) deleted successfully.");

            // --- Clear Vuex State and LocalStorage ---
            commit('CLEAR_AUTH_STATE');
            if (userId) {
                const querentCacheKey = `querentList_${userId}`;
                const readingTypesCacheKey = `readingTypesList_${userId}`;
                //console.log(`[Vuex auth/deleteOAuthUserAccount] Clearing localStorage keys: professionalReadings, ${querentCacheKey}, ${readingTypesCacheKey}`);
                localStorage.removeItem('professionalReadings');
                localStorage.removeItem(querentCacheKey);
                localStorage.removeItem(readingTypesCacheKey);
                // Note: 'mysticlog-lang' is kept.
            } else {
                 console.warn("[Vuex auth/deleteOAuthUserAccount] Could not get userId to clear specific cache keys.");
            }

            commit('SET_LOADING', false);
        } catch (error) {
            console.error("Account deletion failed (OAuth user):", error);
            let specificErrorMessageKey = 'profile.errors.deleteAccountFailed'; // Default

            if (error.code === 'auth/requires-recent-login') {
                specificErrorMessageKey = 'profile.errors.reauthNeededForOAuthDelete';
            }
            commit('SET_ERROR', specificErrorMessageKey);
            commit('SET_LOADING', false);
            throw new Error(specificErrorMessageKey); // Throw the key
        }
    },  

    listenForUnreadSupportTickets({ commit, state }, userId) {
        if (unsubscribeSupportTicketsListener) {
            unsubscribeSupportTicketsListener(); // Unsubscribe from previous listener
        }
        if (!userId) return;

        let q;
        if (state.isAdmin) {
            // Admin: Listen for tickets where admin hasn't read the user's last reply
            q = query(
                collection(db, "supportTickets"),
                where("isReadByAdmin", "==", false),
                where("lastReplyBy", "==", "user") // User was the last to reply
            );
        } else {
            // Regular User: Listen for tickets where user hasn't read admin's last reply
            q = query(
                collection(db, "supportTickets"),
                where("userId", "==", userId),
                where("isReadByUser", "==", false),
                where("lastReplyBy", "==", "admin") // Admin was the last to reply
            );
        }

        unsubscribeSupportTicketsListener = onSnapshot(q, (snapshot) => {
            commit('SET_UNREAD_SUPPORT_MESSAGES_COUNT', snapshot.size);
        }, (error) => {
            console.error("Error listening to unread support tickets:", error);
            commit('SET_UNREAD_SUPPORT_MESSAGES_COUNT', 0);
        });
    },
    stopListeningForUnreadSupportTickets() { // Action to explicitly stop listener
        if (unsubscribeSupportTicketsListener) unsubscribeSupportTicketsListener();
    }
}

// ===
// Private helpers
// ===

/* function saveState(key, state) {
    window.localStorage.setItem(key, JSON.stringify(state))
} */


export default {
    namespaced: true, // Important: Ensure the module is namespaced
    state,
    mutations,
    actions,
    getters
};